import {
  Row,
  Col,
  Typography,
  Table,
  Space,
} from "antd";
import {
  CheckCircleOutlined,
} from "@ant-design/icons";

const { Paragraph, Text } = Typography;

const COLORS = {
  teal: "#00A99E",
  charcoal: "#0A0A0A",
  amber: "#C97A2B",
  lightBg: "#F7F9F9",
  white: "#FFFFFF",
  grey: "#555555",
  border: "#E4E4E4",
};

const SectionHeader = ({ emoji, title }) => (
  <div className="mb-6">
    <h2
      className="text-xl sm:text-2xl font-bold mb-2"
      style={{ color: COLORS.teal }}
    >
      {emoji} {title}
    </h2>
    <div
      className="h-1 w-20 rounded-full"
      style={{ backgroundColor: COLORS.teal }}
    />
  </div>
);

const StatusDot = ({ colour = "teal" }) => (
  <span
    className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 mt-2"
    style={{
      backgroundColor:
        colour === "amber" ? COLORS.amber : COLORS.teal,
    }}
  />
);

const HeroStats = ({ stats = [] }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
    {stats.map((item, index) => (
      <div
        key={index}
        className="rounded-xl px-5 py-4 min-h-[110px] border"
        style={{
          background: "rgba(0,169,158,0.12)",
          borderColor: COLORS.teal,
        }}
      >
        <div
          className="text-2xl sm:text-3xl font-bold break-words"
          style={{ color: COLORS.teal }}
        >
          {item?.value ?? "—"}
        </div>
        <div className="text-sm mt-1 break-words text-gray-600">
          {item?.label}
        </div>
      </div>
    ))}
  </div>
);

const Hero = ({ report }) => {
  const meta = report?.meta || {};

  return (
    <div
      className="rounded overflow-hidden text-white shadow-lg border-b-[6px]"
      style={{
        background:
          "linear-gradient(135deg, #0A0A0A 0%, #1C1C1C 100%)",
        borderBottomColor: COLORS.teal,
      }}
    >
      <div className="px-5 sm:px-8 lg:px-10 py-8 sm:py-10">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 break-words">
            Executive Summary
            {meta.buyer_name ? ` — ${meta.buyer_name}` : ""}
          </h1>

          <div
            className="font-semibold text-base sm:text-lg"
            style={{ color: COLORS.teal }}
          >
            {meta.date_range_display || "—"}
          </div>

          {meta.sub_buyer_count != null && (
            <div className="mt-2 text-sm text-gray-300">
              {meta.sub_buyer_count} Cost Centers
            </div>
          )}
        </div>

        <HeroStats stats={report?.hero_stats} />
      </div>
    </div>
  );
};

const MetricCard = ({ title, children, colour = COLORS.teal }) => (
  <div
    className="bg-white rounded-xl p-5 shadow-sm border-t-4 min-w-0"
    style={{ borderTopColor: colour }}
  >
    <h3
      className="font-bold text-lg mb-4"
      style={{ color: COLORS.charcoal }}
    >
      {title}
    </h3>
    {children}
  </div>
);

const TextList = ({ items = [], colour = "teal" }) => (
  <Space direction="vertical" className="w-full">
    {items.map((item, index) => (
      <div key={index} className="flex items-start gap-2">
        <StatusDot colour={colour} />
        <Text className="leading-6">{item}</Text>
      </div>
    ))}
  </Space>
);
const OneMinuteRead = ({ data }) => {
  if (!data) return null;

  return (
    <section className="w-full max-w-[940px] mx-auto">
      <SectionHeader emoji="1️⃣" title="One-Minute Read" />

      {data.headline && (
        <div className="bg-white rounded-lg px-6 py-5 mb-5 shadow-sm border-l-[5px]"
          style={{ borderLeftColor: COLORS.teal }}
        >
          <h3 className="font-bold text-lg mb-2">
            📊 The Headline
          </h3>
          <Paragraph className="!mb-0 leading-7">
            {data.headline}
          </Paragraph>
        </div>
      )}

      <Row gutter={[20, 20]}>
        <Col xs={24}>
          <MetricCard title="✅ What's Working">
            <TextList items={data.whats_working || []} />
          </MetricCard>
        </Col>

        <Col xs={24}>
          <MetricCard title="🔍 Room to Improve" colour={COLORS.amber}>
            <TextList
              items={data.room_to_improve || []}
              colour="amber"
            />
          </MetricCard>
        </Col>
      </Row>

      {data.opportunity_statement && (
        <div
          className="rounded-lg px-6 py-5 mt-5 text-white"
          style={{ backgroundColor: COLORS.charcoal }}
        >
          <div
            className="font-bold uppercase text-sm tracking-wide mb-2"
            style={{ color: COLORS.teal }}
          >
            🎯 Opportunity
          </div>
          <Paragraph className="!mb-0 !text-white leading-7">
            {data.opportunity_statement}
          </Paragraph>
        </div>
      )}
    </section>
  );
};

const FindingStats = ({ stats = [] }) => {
  if (!stats.length) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 my-5">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="rounded-lg px-4 py-4"
          style={{ backgroundColor: COLORS.lightBg }}
        >
          <div className="text-sm text-gray-600 mb-1">
            {stat?.label}
          </div>
          <div className="text-lg font-semibold break-words">
            {stat?.value ?? "—"}
          </div>
        </div>
      ))}
    </div>
  );
};

const Findings = ({ findings = [] }) => {
  if (!findings.length) return null;

  return (
    <section className="w-full max-w-[940px] mx-auto">
      <SectionHeader emoji="2️⃣" title="Four Key Findings" />

      <Space direction="vertical" size={24} className="w-full">
        {findings.map((finding, index) => (
          <div
            key={finding?.number ?? index}
            className="bg-white rounded-xl px-5 sm:px-7 py-6 shadow-sm border-t-4"
            style={{ borderTopColor: COLORS.teal }}
          >
            <div className="flex items-start gap-3 mb-1">
              <span
                className="flex items-center justify-center w-9 h-9 rounded-full text-white font-bold flex-shrink-0"
                style={{ backgroundColor: COLORS.teal }}
              >
                {finding?.number ?? index + 1}
              </span>

              <h3
                className="text-lg sm:text-xl font-bold m-0 pt-1 break-words"
                style={{ color: COLORS.charcoal }}
              >
                {finding?.title}
              </h3>
            </div>

            {finding?.subheading && (
              <div className="italic text-sm sm:text-base mb-4 sm:ml-[51px]"
                style={{ color: COLORS.grey }}
              >
                {finding.subheading}
              </div>
            )}

            <div
              className="h-0.5 my-4 rounded"
              style={{ backgroundColor: COLORS.border }}
            />

            <FindingStats stats={finding?.stats || []} />

            {finding?.insight && (
              <div className="mt-5">
                <h4 className="uppercase text-sm font-bold mb-2"
                  style={{ color: COLORS.teal }}
                >
                  The Insight
                </h4>
                <Paragraph className="leading-7">
                  {finding.insight}
                </Paragraph>
              </div>
            )}

            {finding?.what_this_means && (
              <div className="mt-5">
                <h4 className="uppercase text-sm font-bold mb-2"
                  style={{ color: COLORS.teal }}
                >
                  What This Means
                </h4>
                <Paragraph className="leading-7">
                  {finding.what_this_means}
                </Paragraph>
              </div>
            )}

            {finding?.what_this_opens_up && (
              <div className="mt-5">
                <h4 className="uppercase text-sm font-bold mb-2"
                  style={{ color: COLORS.teal }}
                >
                  What This Opens Up
                </h4>
                <Paragraph className="leading-7">
                  {finding.what_this_opens_up}
                </Paragraph>
              </div>
            )}

            {(finding?.recommendation_label ||
              finding?.recommendation) && (
                <div
                  className="rounded-lg px-5 py-5 mt-5 text-white"
                  style={{ backgroundColor: COLORS.charcoal }}
                >
                  <div
                    className="uppercase text-sm font-bold mb-2"
                    style={{ color: COLORS.teal }}
                  >
                    🎯 {finding.recommendation_label || "Recommendation"}
                  </div>

                  {finding?.recommendation && (
                    <Paragraph className="!mb-0 !text-white leading-7">
                      {finding.recommendation}
                    </Paragraph>
                  )}
                </div>
              )}
          </div>
        ))}
      </Space>
    </section>
  );
};
const ExecutiveSnapshot = ({ snapshot }) => {
  if (!snapshot) return null;

  return (
    <section className="w-full max-w-[940px] mx-auto">
      <SectionHeader emoji="3️⃣" title="Executive Snapshot" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {snapshot.portfolio_metrics?.length > 0 && (
          <MetricCard title="🎯 Portfolio Metrics">
            <div className="space-y-3">
              {snapshot.portfolio_metrics.map((metric, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between gap-4"
                >
                  <Text>{metric?.label}</Text>
                  <Text strong className="text-right break-words">
                    {metric?.value ?? "—"}
                  </Text>
                </div>
              ))}
            </div>
          </MetricCard>
        )}

        {snapshot.paid_vs_free?.length > 0 && (
          <MetricCard title="⚖️ Paid vs Free">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {snapshot.paid_vs_free.map((item, index) => (
                <div
                  key={index}
                  className="rounded-lg p-4"
                  style={{ backgroundColor: COLORS.lightBg }}
                >
                  <div className="text-sm text-gray-600 mb-1">
                    {item?.label}
                  </div>
                  <div className="font-bold text-lg break-words">
                    {item?.value ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          </MetricCard>
        )}

        {snapshot.top_occupations?.length > 0 && (
          <MetricCard title="🏆 Top Occupations">
            <div className="space-y-3">
              {snapshot.top_occupations.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <span
                    className="flex items-center justify-center w-7 h-7 rounded-full text-white font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: COLORS.teal }}
                  >
                    {item?.rank ?? index + 1}
                  </span>

                  <div className="min-w-0">
                    <Text strong className="block break-words">
                      {item?.occupation}
                    </Text>
                    {item?.note && (
                      <Text type="secondary">{item.note}</Text>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </MetricCard>
        )}

        {snapshot.areas_to_explore?.length > 0 && (
          <MetricCard title="🔍 Areas to Explore" colour={COLORS.amber}>
            <TextList
              items={snapshot.areas_to_explore}
              colour="amber"
            />
          </MetricCard>
        )}
      </div>

      {snapshot.highlights && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          {[
            {
              title: "💪 Strongest Site",
              item: snapshot.highlights.strongest_site,
              color: COLORS.teal,
            },
            {
              title: "🔎 Site to Explore",
              item: snapshot.highlights.weakest_site,
              color: COLORS.amber,
            },
          ].map(({ title, item, color }) =>
            item ? (
              <MetricCard key={title} title={title} colour={color}>
                <Text strong className="block text-base mb-1">
                  {item.site || "—"}
                </Text>
                <Text type="secondary">{item.note || "—"}</Text>
              </MetricCard>
            ) : null
          )}
        </div>
      )}
    </section>
  );
};
const CostCenters = ({ data }) => {
  if (!data) return null;

  const headers = data.table?.headers || [];

  const columns = headers.map((header, index) => ({
    title: header,
    dataIndex: `col${index}`,
    key: `col${index}`,
    render: (value) => value ?? "—",
  }));

  const tableData = (data.table?.rows || []).map((row, rowIndex) => {
    const record = { key: rowIndex };

    row.forEach((cell, cellIndex) => {
      record[`col${cellIndex}`] = cell;
    });

    return record;
  });

  const highlights = data.highlights || [];

  return (
    <section className="w-full max-w-[940px] mx-auto">
      <SectionHeader emoji="🏢" title="Cost Center Performance" />

      {data.intro && (
        <div className="bg-white rounded-lg px-6 py-5 mb-5 shadow-sm border-l-[5px]"
          style={{ borderLeftColor: COLORS.teal }}
        >
          <Paragraph className="!mb-0 leading-7">
            {data.intro}
          </Paragraph>
        </div>
      )}

      {highlights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          {highlights.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-5 shadow-sm border-t-4"
              style={{
                borderTopColor:
                  index === 2 ? COLORS.amber : COLORS.teal,
              }}
            >
              <div className="text-sm text-gray-500 mb-2">
                {item?.label}
              </div>
              <div className="font-semibold leading-6 break-words">
                {item?.value ?? "—"}
              </div>
            </div>
          ))}
        </div>
      )}

      {data.table && (
        <div className="bg-white rounded-xl p-5 mb-5 shadow-sm border-t-4 overflow-hidden"
          style={{ borderTopColor: COLORS.teal }}
        >
          <h3 className="font-bold text-lg mb-4">
            Site-by-Site Comparison
          </h3>

          <Table
            columns={columns}
            dataSource={tableData}
            bordered
            size="middle"
            pagination={false}
            scroll={{ x: "max-content" }}
          />

          {data.table.omitted_note && (
            <Paragraph className="mt-4 mb-0 text-sm text-gray-500">
              {data.table.omitted_note}
            </Paragraph>
          )}
        </div>
      )}

      {data.insight && (
        <MetricCard title="📊 Site Performance Insight">
          <Paragraph className="!mb-0 leading-7">
            {data.insight}
          </Paragraph>
        </MetricCard>
      )}

      {data.what_this_means && (
        <div className="mt-5">
          <MetricCard title="What This Means">
            <Paragraph className="!mb-0 leading-7">
              {data.what_this_means}
            </Paragraph>
          </MetricCard>
        </div>
      )}

      {data.what_this_opens_up && (
        <div className="mt-5">
          <MetricCard title="What This Opens Up">
            <Paragraph className="!mb-0 leading-7">
              {data.what_this_opens_up}
            </Paragraph>
          </MetricCard>
        </div>
      )}

      {(data.recommendation_label || data.recommendation) && (
        <div
          className="rounded-lg px-5 py-5 mt-5 text-white"
          style={{ backgroundColor: COLORS.charcoal }}
        >
          <div
            className="uppercase text-sm tracking-wide font-bold mb-2"
            style={{ color: COLORS.teal }}
          >
            🎯 {data.recommendation_label || "Recommendation"}
          </div>

          {data.recommendation && (
            <Paragraph className="!mb-0 !text-white leading-7">
              {data.recommendation}
            </Paragraph>
          )}
        </div>
      )}
    </section>
  );
};
const WhatsWorking = ({ items = [] }) => {
  if (!items.length) return null;

  return (
    <section className="w-full max-w-[940px] mx-auto">
      <SectionHeader
        emoji="4️⃣"
        title="What's Working — Build on This"
      />

      <Space direction="vertical" size={18} className="w-full">
        {items.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-lg px-5 sm:px-6 py-5 shadow-sm border-l-[5px]"
            style={{ borderLeftColor: COLORS.teal }}
          >
            <div className="flex items-start gap-3">
              <CheckCircleOutlined
                className="text-lg mt-1 flex-shrink-0"
                style={{ color: COLORS.teal }}
              />

              <div className="min-w-0">
                <h3
                  className="font-bold text-base sm:text-lg mb-2 break-words"
                  style={{ color: COLORS.charcoal }}
                >
                  {item?.label}
                </h3>

                <Paragraph className="!mb-0 leading-7">
                  {item?.detail}
                </Paragraph>
              </div>
            </div>
          </div>
        ))}
      </Space>
    </section>
  );
};

const WhyNow = ({ data }) => {
  if (!data) return null;

  return (
    <section className="w-full max-w-[940px] mx-auto pb-2">
      <SectionHeader
        emoji="5️⃣"
        title="Why Now — Fine-Tuning, Not Overhaul"
      />

      <div
        className="bg-white rounded-xl px-5 sm:px-8 py-6 shadow-sm border-t-[5px]"
        style={{ borderTopColor: COLORS.teal }}
      >
        {data.opening_statement && (
          <Paragraph className="text-base leading-7 text-gray-700">
            {data.opening_statement}
          </Paragraph>
        )}

        {data.recommendations_recap?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-bold text-lg mb-4">
              🎯 Recommendations Recap
            </h3>

            <ul className="pl-5 space-y-3">
              {data.recommendations_recap.map((item, index) => (
                <li
                  key={index}
                  className="leading-6 font-medium text-gray-700"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.expected_outcome && (
          <div
            className="rounded-lg px-5 py-4 mt-6 text-white text-center font-bold text-base sm:text-lg"
            style={{ backgroundColor: COLORS.teal }}
          >
            ✨ {data.expected_outcome}
          </div>
        )}
      </div>
    </section>
  );
};

const AnalysisMasterSummary = ({ report }) => {
  if (!report) return null;

  return (
    <div
      className="w-full max-w-full overflow-x-hidden space-y-8"
      style={{
        backgroundColor: COLORS.lightBg,
        color: COLORS.charcoal,
      }}
    >
      {/* 1. Hero and group statistics */}
      <Hero report={report} />

      {/* 2. One-Minute Read */}
      <OneMinuteRead data={report.one_minute_read} />

      {/* 3. Four Key Findings */}
      <Findings findings={report.findings || []} />

      {/* 4. Executive Snapshot with site highlights */}
      <ExecutiveSnapshot snapshot={report.executive_snapshot} />

      {/* 5. What's Working */}
      <WhatsWorking items={report.whats_working || []} />

      {/* 6. Master Buyer site-by-site performance */}
      <CostCenters data={report.cost_centers} />

      {/* 7. Why Now */}
      <WhyNow data={report.why_now} />
    </div>
  );
};

export default AnalysisMasterSummary;